import React, { useRef, useState, useEffect } from 'react';

import { Button, Link, styled } from '@asicsdigital/oneasics-blocks';
import { decode } from 'html-entities';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTracking } from 'react-tracking';

import { getTranslatedPronationText } from '../../utils/utils';
import AccordionRow, { Accordion } from '../../views/accordion-row/AccordionRow';

const HitWrapper = styled('div', {
  borderBottom: '1px solid $grey300',
  color: '$textPrimary',
  marginBottom: '20px',
  paddingBottom: '20px',
  textAlign: 'center',
  button: {
    fontFamily: '$asics',
    fontSize: '15px',
    fontWeight: '$bold',
    letterSpacing: '0.4px',
    marginTop: '28px',
    width: '100%',
    '&:focus': {
      outline: '2px solid #001e63',
      backgroundColor: '$primaryHover',
    },
  },
  '@tablet': {
    display: 'flex',
    textAlign: 'left',
  },
});

const Badge = styled('div', {
  backgroundColor: '$primary',
  borderRadius: '5px',
  color: '$white',
  fontWeight: '$bold',
  fontSize: '$small',
  letterSpacing: '0.4px',
  padding: '5px 10px',
  width: 'initial',
  display: 'inline-block',

  '@tablet': {
    width: 'fit-content',
  },
});

const MobileBadge = styled(Badge, {
  display: 'block',
  '@tablet': {
    display: 'none',
  },
});

const TabletDesktopBadge = styled(Badge, {
  display: 'none',
  '@tablet': {
    display: 'block',
    margin: 'unset',
  },
});

const Name = styled('h2', {
  fontFamily: '$asics',
  fontSize: '$xxLarge',
  fontWeight: '$bold',

  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
});

const MobileName = styled(Name, {
  marginBottom: '10px',
  marginTop: '5px',
  textDecoration: 'underline',
  fontSize: '$xxLarge',
  lineHeight: '29px',

  '@tablet': {
    display: 'none',
  },
});

const TabletDesktopName = styled(Name, {
  display: 'none',
  marginBottom: '5px',
  fontSize: '30px',
  lineHeight: '33px',

  '@tablet': {
    display: 'block',
  },
});

const PrettyCategoryName = styled('div', {
  marginBottom: '10px',
  fontSize: '15px',
  lineHeight: '20px',
  fontWeight: '$regular',
});

const MobilePrettyCategoryName = styled(PrettyCategoryName, {
  
  '@tablet': {
    display: 'none',
  },
});

const TabletPrettyCategoryName = styled(PrettyCategoryName, {
  display: 'none',

  '@tablet': {
    display: 'block',
  },
});

const Price = styled('div', {
  fontSize: '15px',
  fontWeight: '$bold',
  lineHeight: '22px',
  '@tablet': {
    fontSize: '$large',
  },
});

const MobilePrice = styled(Price, {
  display: 'inline-block',
  '@tablet': {
    display: 'none',
  },
});

const MobileSalePrice = styled(Price, {
  display: 'inline-block',
  color: '#b9411d',
  marginLeft: '10px',

  '@tablet': {
    display: 'none',
  },
});

const TabletDesktopPrice = styled(Price, {
  display: 'none',
  '@tablet': {
    display: 'inline-block',
  },
});

const TabletDesktopSalePrice = styled(Price, {
  color: '#b9411d',
  marginLeft: '10px',
  display: 'none',
  '@tablet': {
    display: 'inline-block',
  },
});

const StarCollection = styled('div', {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
});

const StarCollectionMobile = styled(StarCollection, {
  display: 'flex',
  margin: '5px 0 5px 0',

  '@tablet': {
    display: 'none',
  },
});

const StarCollectionTablet = styled(StarCollection, {
  display: 'none',
  justifyContent: 'initial',
  margin: '0 0 5px 10px',

  '@tablet': {
    display: 'inline-block',
  },
});

const StarRatingContainer = styled('div', {
  display: 'flex',
  position: 'absolute',
  width: '75px',
});

const StarRatingBackgroundContainer = styled(StarRatingContainer, {
  justifyContent: 'flex-end',
  position: 'initial',
});

const StarRating = styled('div', {
  img: {
    height: '15px',
    width: '15px',
  },
});

const StarRatingBackground = styled(StarRating, {

});

const StarRatingPercentage = styled(StarRating, {
  display: 'flex',

  img: {
    objectFit: 'cover', 
    objectPosition: '0 100%',
  },
});

const HeroAndCarouselWrapper = styled('div', {
  textAlign: 'center',

  '@tablet': {
    width: '50%',
  },
});

const ResultHero = styled('img', {
  width: '70%',

  '&:hover': {
    cursor: 'pointer',
  },
});

const Carousel = styled('div', {
  display: 'flex',
  justifyContent: 'center',
});

const ArrowIcon = styled('img', {
  display: 'none',

  '&:hover': {
    cursor: 'pointer',
  },

  '@desktop': {
    display: 'block',
  },
});

const CarouselImagesContainer = styled('div', {
  display: 'flex',
  overflow: 'scroll',
  scrollbarWidth: 'none',
  marginLeft: 'auto',
  marginRight: 'auto',
  maxWidth: '375px',
  paddingBottom: '15px',

  '@desktop': {
    maxWidth: '420px',
    paddingBottom: 'unset',

    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
});

const Color = styled('div', {
  fontWeight: '$regular',
  fontSize: '15px',
});

const VariantOption = styled('img', {
  height: '55px',
  marginLeft: '5px',
  marginRight: '5px',
  width: '65px',

  '&:hover': {
    cursor: 'pointer',
  },

  '@desktop': {
    width: '75px',
  },

  variants: {
    selected: {
      true: {
        borderBottom: '4px solid $secondary',
      },
    },
  },
});

const ProductDescriptionWrapper = styled('div', {
  '@tablet': {
    paddingLeft: '30px',
    paddingRight: '30px',
    width: '50%',
  },
});

const ProductCtaWrapper = styled('div', {
  width: '100%',

  '@tablet': {
    width: '90%',
  },
});

const Description = styled('div', {
  fontSize: '15px',
  fontWeight: '$regular',
  lineHeight: '20px',
  marginBottom: '15px',
  marginLeft: 'auto',
  marginRight: 'auto',
  marginTop: '5px',
  width: '90%',

  '@tablet': {
    marginTop: '15px',
    width: 'unset',
  },
});

const MadeForSection = styled('div', {
  paddingBottom: '25px',
});

const MadeForSectionTitle = styled('div', {
  fontSize: '15px',
  fontWeight: '$bold',
  marginBottom: '10px',
  '@tablet': {
    fontSize: '$large',
  },
});

const MadeForSectionIconsWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  textAlign: 'center',

  '@tablet': {
    justifyContent: 'initial',
  },
});

const MadeForIcon = styled('span', {
  marginRight: '5px',
  width: '100px',
  fontSize: '15px',
  lineHeight: '20px',

  img: {
    height: '80px',
    width: '80px',
  },
});

const SpecRow = styled('div', {
  display: 'flex',
  marginBottom: '10px',
  fontSize: '15px',

  div: {
    '&:first-child': {
      minWidth: '130px',
      fontWeight: '$bold',
    },
    '&:nth-child(2)': {
      marginLeft: '30px',
      fontWeight: '$regular',
    },
  },
});

const GatedBadgeContainer = styled('div', {
  position: 'relative',
});

const MobileGatedBadgeContainer = styled(GatedBadgeContainer, {
  display: 'block',
  '@tablet': {
    display: 'none',
  },
});

const DesktopGatedBadgeContainer = styled(GatedBadgeContainer, {
  display: 'none',
  '@tablet': {
    display: 'block',
  },
});

const BadgeContainer = styled('div', {
  justifyContent: 'space-between',
  alignItems: 'center',
  display: 'flex',
  width: '85%',
  marginLeft: 'auto',
  marginRight: 'auto',
  '@tablet': {
    width: 'initial',
    marginLeft: 'initial',
    marginRight: 'initial',
  },
});


const GatedBadgeContent = styled('div', {
  right: '5%',
  top: '0',
  padding: '9px 35px 13px 0',
  overflow: 'hidden',
  zIndex: '1',
  '@tablet': {
    position: 'absolute',
  },
});

const GatedBadgeText = styled('div', {
  fontFamily: '$asics',
  fontSize: '13px',
  display: 'inline-block',
  padding: '5px 18px 5px 10px',
  background:'#f1f2f2',
  borderBottomLeftRadius: '15px',
  borderTopLeftRadius: '15px',
  color: '$textSecondary',
});

const GatedBadgeImg = styled('div', {
  backgroundColor: '#fff',
  borderRadius: '50%',
  boxShadow: '0 2px 6px #e3e5e5',
  height: '41px',
  position: 'absolute',
  right: '5px',
  top: '0',
  width: '41px',
  zIndex: '1',
});


/**
 * A response interpretation of a shoe from Algolia.
 */
const Hit = ({
  bazaarvoice,
  colorPalette,
  currentCurrency,
  feel,
  gated,
  genericBadge,
  heelDrop,
  hookContent,
  locale,
  masterId,
  name,
  otherVariants,
  prettyCategoryName,
  pronation,
  purpose,
  resultsPageData,
  saleDates,
  salePrice,
  standardPrice,
  surface,
  urlSearchParams,
  weightGrams,
  weightOz,
  width,
}) => {
  const carouselImages = useRef(null);
  const { questions } = useSelector((state) => state.wizards);
  const [selectedVariant, setSelectedVariant] = useState(otherVariants?.filter((variant) => variant.currentVariant)[0]);
  const { Track, trackEvent } = useTracking();

  useEffect(() => {
    trackEvent({
      gaEventAction: 'product_impression',
      gaEventLabel: `${masterId} - ${name}`,
      gaEventNonInteraction: true,
      sf2_colors: colorPalette,
      sf2_section_label: 'results',
      sf2_product_rec: masterId.replace('ANA_', ''),
      sf2_pronation: pronation ? pronation.toString() : '',
      sf2_feel: feel,
      sf2_size_width: width,
    });
  }, []);

  const slideCarouselLeft = () => {
    carouselImages.current.scrollLeft += -172; // TODO: remove magic numbers eventually
  };
  const slideCarouselRight = () => {
    carouselImages.current.scrollLeft += 172; // TODO: remove magic numbers eventually
  };
  const shouldDisplayArrows = otherVariants.length > 5; // TODO: remove magic number eventually

  const sortAlphabetically = (a, b) => {
    return a.localeCompare(b);
  };

  // converting to a number
  const bazaarvoiceToNumber = bazaarvoice ? Number(bazaarvoice) : 0;

  const purposeQuestion = Object.values(questions).find((question) => question.facet === 'purpose');

  // Yay custom logic for displaying purpose made for icon
  let madeForPurpose = purpose[0]?.toLowerCase();
  if (purpose.length > 1) {
    purpose.forEach((shoePurpose) => {
      const lowerCasePurpose = shoePurpose?.toLowerCase();
      if (purposeQuestion?.answer === lowerCasePurpose || urlSearchParams?.get('filters')?.includes(lowerCasePurpose)) {
        madeForPurpose = lowerCasePurpose;
      }
    });
  }

  const madeForPronation = pronation?.sort(sortAlphabetically).join(',')?.toLowerCase();
  const madeForFeel = feel?.toLowerCase();

  const madeForFacets = [{
    facet: 'purpose',
    value: madeForPurpose,
  }, {
    facet: 'pronation',
    value: madeForPronation,
  }, {
    facet: 'feel',
    value: madeForFeel,
  }];

  const translatedPronationText = getTranslatedPronationText(madeForFacets, resultsPageData);

  // check if the sales begin date and sales end date is valid
  const currentDate = new Date().getTime();
  const saleDateBegin = saleDates?.saleDateBegin;
  const saleDateEnd = saleDates?.saleDateEnd;

  let isSaleDateValid = false;
  if (saleDateBegin <= currentDate && saleDateEnd >= currentDate) {
    isSaleDateValid = true;
  }
  
  const currencyCode = currentCurrency?.currencyCode;

  let useSalePrice;
  if (salePrice) {
    useSalePrice = new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode  }).format(salePrice);
  }
  else {
    useSalePrice = resultsPageData?.unavailablePricing;
  }

  let useStandardPrice;
  if (standardPrice) {
    useStandardPrice = new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode  }).format(standardPrice);
  }
  else {
    useStandardPrice = resultsPageData?.unavailablePricing;
  }

  const trackClick = (gaEventAction) => {
    trackEvent({
      gaEventAction,
      gaEventLabel: `${masterId} - ${name}`,
      gaEventNonInteraction: false,
      sf2_section_label: 'results',
      sf2_product_rec: masterId,
    });
  };

  const remainderPercentage = () => {
    let starPercentage = bazaarvoiceToNumber % 1;
    starPercentage = starPercentage.toFixed(2);
    starPercentage *= 100;
    return starPercentage;
  };

  const handleCtaClick = () => {
    trackClick('product_click | 100%');
    window.open(selectedVariant?.url);
  };

  const resultHeroAccessibilityText = decode(selectedVariant?.image?.alt);

  const carouselAccessibilityText = (variant) => {
    return decode(variant?.image?.alt);
  };

  const StarsBackground = () => {
    const bazaarVoiceData = new Array(Math.floor(bazaarvoiceToNumber)).fill(0);
    const stars = bazaarVoiceData.map((ele, index) => {
      const key = `${resultsPageData?.starRating?.inactiveStar?.title}-${index}`;
      return  <StarRatingBackground key={key}><img alt={resultsPageData?.starRating?.inactiveStar?.title} src={resultsPageData?.starRating?.inactiveStar?.url}/></StarRatingBackground>;
    });
    return stars;
  };

  const Stars = () => {
    const bazaarVoiceData = new Array(Math.floor(bazaarvoiceToNumber)).fill(0);
    const stars = bazaarVoiceData.map((ele, index) => {
      const key = `${resultsPageData?.starRating?.activeStar?.title}-${index}`;
      return <StarRating key={key}><img alt={resultsPageData?.starRating?.activeStar?.title} src={resultsPageData?.starRating?.activeStar?.url}/></StarRating>;
    });
    return stars;
  };

  return (
    <Track>
      <HitWrapper>
        <BadgeContainer>
        {isSaleDateValid && (
            <MobileBadge css={{
              backgroundColor: '#b9411d',
            }}>
              Sale
            </MobileBadge>
          )}
          {!isSaleDateValid && genericBadge?.enableGenericBadgeText && (
            <MobileBadge css={{
              backgroundColor: genericBadge?.genericBadgeBackgroundColor,
              color: genericBadge?.genericBadgeColor,
            }}>
              {genericBadge?.genericBadgeText}
            </MobileBadge>
          )}

          {gated?.isGated && (
            <MobileGatedBadgeContainer>
              <GatedBadgeContent>
                <GatedBadgeText><div>{resultsPageData?.gatedBadge?.title}</div></GatedBadgeText>
                <GatedBadgeImg><div><img alt={resultsPageData?.gatedBadge?.gatedBadgeMedia?.title} src={resultsPageData?.gatedBadge?.gatedBadgeMedia?.url}/></div></GatedBadgeImg>
              </GatedBadgeContent>
            </MobileGatedBadgeContainer>
          )}
        </BadgeContainer>

        <MobileName onClick={handleCtaClick}>{name}</MobileName>
        <MobilePrettyCategoryName>{prettyCategoryName.split(' ').map((elem) => elem.charAt(0).toUpperCase() + elem.slice(1)).join(' ')}</MobilePrettyCategoryName>
        <MobilePrice style={ isSaleDateValid ? { textDecoration:'line-through' } : {}}>{useStandardPrice}</MobilePrice>
          {isSaleDateValid && (
            <MobileSalePrice>{`${useSalePrice}`}</MobileSalePrice>
          )}
        <StarCollectionMobile>
            <StarRatingContainer>
              <Stars />
              <StarRatingPercentage><img alt={resultsPageData?.starRating?.activeStar?.title} src={resultsPageData?.starRating?.activeStar?.url} style={{ width: `${remainderPercentage()}%` }}/></StarRatingPercentage>
            </StarRatingContainer>
            <StarRatingBackgroundContainer>
              <StarsBackground />
            </StarRatingBackgroundContainer>
        </StarCollectionMobile>
        

        <HeroAndCarouselWrapper>
        
        {gated?.isGated && (
          <DesktopGatedBadgeContainer>
            <GatedBadgeContent>
              <GatedBadgeText><div>{resultsPageData?.gatedBadge?.title}</div></GatedBadgeText>
              <GatedBadgeImg>
                <div>
                  <img alt={resultsPageData?.gatedBadge?.gatedBadgeMedia?.title} src={resultsPageData?.gatedBadge?.gatedBadgeMedia?.url}/>
                </div>
              </GatedBadgeImg>
            </GatedBadgeContent>
          </DesktopGatedBadgeContainer>
        )}
          {isSaleDateValid && (
            <TabletDesktopBadge css={{
              backgroundColor: '#b9411d',
            }}>
              Sale
            </TabletDesktopBadge>
          )}

          {!isSaleDateValid && genericBadge?.enableGenericBadgeText && (
            <TabletDesktopBadge css={{
              backgroundColor: genericBadge?.genericBadgeBackgroundColor,
              color: genericBadge?.genericBadgeColor,
            }}>
              {genericBadge?.genericBadgeText}
            </TabletDesktopBadge>
          )}
          <ResultHero 
            alt={resultHeroAccessibilityText}
            src={selectedVariant?.image?.url} 
            title={resultHeroAccessibilityText}
            onClick={handleCtaClick}
          />
          <Color>
            {otherVariants.length} {otherVariants.length > 1 ? resultsPageData.colorsText: resultsPageData.colorText}
          </Color>


          <Carousel>
            {shouldDisplayArrows && (
              <ArrowIcon
                alt={resultsPageData?.carouselLeftArrow?.title}
                src={resultsPageData?.carouselLeftArrow?.url}
                tabIndex={0}
                onClick={slideCarouselLeft}
              />
            )}
            <CarouselImagesContainer ref={carouselImages}>
              {otherVariants.map((variant) => (
                <VariantOption
                  alt={carouselAccessibilityText(variant)}
                  key={variant.image.alt}
                  selected={selectedVariant === variant}
                  src={variant.image.url}
                  tabIndex={0}
                  title={carouselAccessibilityText(variant)}
                  onClick={() => setSelectedVariant(variant)}
                />
              ))}
            </CarouselImagesContainer>
            {shouldDisplayArrows && (
              <ArrowIcon
                alt={resultsPageData?.carouselRightArrow?.title}
                src={resultsPageData?.carouselRightArrow?.url}
                tabIndex={0}
                onClick={slideCarouselRight}
              />
            )}
          </Carousel>
        </HeroAndCarouselWrapper>

        <ProductDescriptionWrapper>
          <TabletDesktopName onClick={handleCtaClick}>{name}</TabletDesktopName>
          {prettyCategoryName && (
            <TabletPrettyCategoryName>{prettyCategoryName.split(' ').map((elem) => elem.charAt(0).toUpperCase() + elem.slice(1)).join(' ')}</TabletPrettyCategoryName>
          )}
          <TabletDesktopPrice style={ isSaleDateValid ? { textDecoration:'line-through' } : {}} >{useStandardPrice}</TabletDesktopPrice>
              {isSaleDateValid && (
                 <TabletDesktopSalePrice>{useSalePrice}</TabletDesktopSalePrice>
              )}   

          <StarCollectionTablet>
            <StarRatingContainer>
            <Stars />
            <StarRatingPercentage><img alt={resultsPageData?.starRating?.activeStar?.title} src={resultsPageData?.starRating?.activeStar?.url} style={{ width: `${remainderPercentage()}%` }}/></StarRatingPercentage>
            </StarRatingContainer>
            <StarRatingBackgroundContainer>
              <StarsBackground />
            </StarRatingBackgroundContainer>
          </StarCollectionTablet>       
          
          <Description>{hookContent}</Description>
          
          {(madeForPurpose || madeForPronation || madeForFeel) && (
            <MadeForSection>
              <MadeForSectionTitle>{resultsPageData?.madeFor?.title}</MadeForSectionTitle>
              <MadeForSectionIconsWrapper>
                {madeForFacets.map((madeForFacet) => {
                  const madeForIconData = resultsPageData?.madeFor?.iconsCollection?.items.filter((iconData) => (
                    madeForFacet.facet === iconData.facet?.toLowerCase() &&
                    madeForFacet.value === iconData.value?.toLowerCase()
                  ))[0];

                  return madeForIconData && (
                    <MadeForIcon key={madeForIconData.title}>
                      <img alt={madeForIconData.icon.title} src={madeForIconData.icon.url} />
                      <div>{madeForIconData.title}</div>
                    </MadeForIcon>
                  );
                })}
              </MadeForSectionIconsWrapper>
            </MadeForSection>
          )}

          <Accordion>
            {(
              surface.length > 0 ||
              heelDrop ||
              (weightGrams && weightOz) ||
              pronation.length > 0 ||
              feel
            ) && (
              <AccordionRow
                name="specs"
                rowTitle={resultsPageData?.accordionRowsCollection?.items?.[0]?.title}
              >
                {surface.length > 0 && (
                  <SpecRow>
                    <div>{resultsPageData?.accordionRowsCollection?.items?.[0]?.surfaceTitle}</div>
                    <div>
                      {surface.map((surf) => surf.charAt(0).toUpperCase() + surf.slice(1)).join(', ')}
                    </div>
                  </SpecRow>
                )}
                {heelDrop && (
                  <SpecRow>
                    <div>{resultsPageData?.accordionRowsCollection?.items?.[0]?.heelDropTitle}</div>
                    <div>{heelDrop}</div>
                  </SpecRow>
                )}
                {(weightGrams && weightOz) && (
                  <SpecRow>
                    <div>{resultsPageData?.accordionRowsCollection?.items?.[0]?.weightTitle}</div>
                    <div>{weightGrams} / {weightOz}</div>
                  </SpecRow>
                )}
                {pronation.length > 0 && (
                  <SpecRow>
                    <div>{resultsPageData?.accordionRowsCollection?.items?.[0]?.supportTitle}</div>
                    <div>{translatedPronationText}</div>
                  </SpecRow>
                )}
                {feel && (
                  <SpecRow>
                    <div>{resultsPageData?.accordionRowsCollection?.items?.[0]?.cushionTitle}</div>
                    <div>{feel}</div>
                  </SpecRow>
                )}
              </AccordionRow>
            )}

            <AccordionRow
              name="reviews"
              rowTitle={resultsPageData?.accordionRowsCollection?.items?.[1]?.title}
            >
              <Link // eslint-disable-line jsx-a11y/anchor-is-valid
                id="reviews-link"
                linkType="external"
                name="reviews-link"
                newTab="true"
                path={`${selectedVariant?.url}#BVRRContainer`}
                onClick={() => {trackClick('reviews_click | 100%');}}
              >
                {resultsPageData?.accordionRowsCollection?.items?.[1]?.linkText}
              </Link>
            </AccordionRow>
          </Accordion>
          <ProductCtaWrapper>
            <Button
                color="primary"
                id={`shop-${name}`}
                name={`shop-${name}`}
                onClick={handleCtaClick}
            >
              {resultsPageData.productCtaTextLocation ? `${resultsPageData.productCtaText} ${name}` : `${name} ${resultsPageData.productCtaText}`}  
            </Button>
          </ProductCtaWrapper>

        </ProductDescriptionWrapper>
      </HitWrapper>
    </Track>
  );
};

Hit.propTypes = {
  /**
   * The bazaar rating of a product
   */
  bazaarvoice: PropTypes.string,
  /**
   * The width of the shoes.
   */
  colorPalette: PropTypes.string,
  /**
   * The current currency of the shoe.
   */
  currentCurrency: PropTypes.shape({
    currencyCode: PropTypes.string,
  }),
  /**
   * The feel of the shoe.
   */
  feel: PropTypes.string,
  /**
   * The gated information on a product.
   */
  gated: PropTypes.shape({
    gatedEndDate: PropTypes.number,
    isGated: PropTypes.bool,
  }),
  /**
   * The badge information on a product.
   */
   genericBadge: PropTypes.shape({
    enableGenericBadgeText: PropTypes.bool,
    genericBadgeBackgroundColor: PropTypes.string,
    genericBadgeColor: PropTypes.string,
    genericBadgeText: PropTypes.string,
  }),
  /**
   * The heel drop of the shoe.
   */
  heelDrop: PropTypes.string,
  /**
   * The short description of the product.
   */
  hookContent: PropTypes.string,
  /**
   * The locale used for currency
   */
  locale: PropTypes.string,
  /**
   * The id of the product with no color code.
   */
  masterId: PropTypes.string,
  /**
   * The product name.
   */
  name: PropTypes.string,
  /**
   * The list of other product variations.
   */
  otherVariants: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.string,
    image: PropTypes.shape({
      alt: PropTypes.string,
      url: PropTypes.string,
    }),
    productColor: PropTypes.string,
    url: PropTypes.string,
  })),
  /**
   * The pretty category name name.
   */
  prettyCategoryName: PropTypes.string,
  /**
   * The pronation of the shoe.
   */
  pronation: PropTypes.arrayOf(PropTypes.string),
  /**
   * The purpose of the shoe.
   */
  purpose: PropTypes.arrayOf(PropTypes.string),
  /**
   * The contentful data for the page.
   */
   resultsPageData: PropTypes.shape({
    accordionRowsCollection: PropTypes.shape({
      items: PropTypes.arrayOf(PropTypes.shape({
        cushionTitle: PropTypes.string,
        heelDropTitle: PropTypes.string,
        linkText: PropTypes.string,
        supportTitle: PropTypes.string,
        surfaceTitle: PropTypes.string,
        title: PropTypes.string,
        weightTitle: PropTypes.string,
      })),
    }),
    carouselLeftArrow: PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    }),
    carouselRightArrow: PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    }),
    closeMatchText: PropTypes.string,
    colorsText: PropTypes.string,
    colorText: PropTypes.string,
    editAnswersText: PropTypes.string,
    gatedBadge: PropTypes.shape({
      gatedBadgeMedia: PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
      title: PropTypes.string,
    }),
    madeFor: PropTypes.shape({
      iconsCollection: PropTypes.shape({
        items: PropTypes.arrayOf(PropTypes.shape({
          facet: PropTypes.string,
          icon: PropTypes.shape({
            title: PropTypes.string,
            url: PropTypes.string,
          }),
          title: PropTypes.string,
          value: PropTypes.string,
        })),
      }),
      title: PropTypes.string,
    }),
    noResultsErrorText: PropTypes.string,
    perfectMatchText: PropTypes.string,
    productCtaText: PropTypes.string,
    productCtaTextLocation: PropTypes.bool,
    restartQuizText: PropTypes.string,
    starRating: PropTypes.shape({
      activeStar: PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
      inactiveStar: PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
      title: PropTypes.string,
    }),
    unavailablePricing: PropTypes.string,
  }),
  /**
   * The date of the sale.
   */
  saleDates: PropTypes.shape({
    saleDateBegin: PropTypes.number,
    saleDateEnd: PropTypes.number,
  }),
   /**
   * The sale price of the product.
   */
  salePrice: PropTypes.number,
  /**
   * The standard price of the shoe.
   */
  standardPrice: PropTypes.number,
  /**
   * The surface the shoe is made for.
   */
  surface: PropTypes.arrayOf(PropTypes.string),
  /**
   *  The search params for the url
   */
  urlSearchParams: PropTypes.objectOf(PropTypes.string),
  /**
   * The weight of the shoes in grams.
   */
  weightGrams: PropTypes.string,
  /**
   * The weight of the shoes in ounces.
   */
  weightOz: PropTypes.string,
  /**
   * The width of the shoes.
   */
  width: PropTypes.string,
};

Hit.defaultProps = {
  bazaarvoice: null,
  colorPalette: '',
  currentCurrency: {},
  feel: '',
  gated: {},
  genericBadge: {},
  heelDrop: '',
  hookContent: '',
  locale: '',
  masterId: '',
  name: '',
  otherVariants: [],
  prettyCategoryName:'',
  pronation: [],
  purpose: [],
  resultsPageData: {},
  saleDates: {},
  salePrice: null,
  standardPrice: null,
  surface: '',
  urlSearchParams: {},
  weightGrams: '',
  weightOz: '',
  width: '',
};

export default Hit;